/**
 * bellCatalogue.ts — All 16 synthesized instruments, grouped by tradition.
 *
 * Naming: English name primary, traditional name (script) as subtitle.
 * IDs match BellInstrumentId from @retreat/core exactly.
 *
 * Used by BellLibraryScreen (selection) and BellPreviewScreen (detail).
 */
import type { BellInstrumentId } from "@retreat/core";

export type BellEntry = {
  id: BellInstrumentId;
  tradition: string;
  name: string;             // English-first, human-readable
  traditionalName: string;  // Script name + romanization (secondary)
  use: string;
  decayLabel: string;
  character: string;
};

export const BELL_CATALOGUE: BellEntry[] = [
  // ── Japanese Zen ────────────────────────────────────────────────────────────
  {
    id: "rin",
    tradition: "Japanese Zen",
    name: "Meditation Bowl",
    traditionalName: "Rin (鈴)",
    use: "zazen opening · interval · universal",
    decayLabel: "7 s",
    character: "clear · bronze · present",
  },
  {
    id: "keisu",
    tradition: "Japanese Zen",
    name: "Altar Bowl",
    traditionalName: "Keisu (磬子)",
    use: "opening · closing · ceremony",
    decayLabel: "9 s",
    character: "low · long · grounding",
  },
  {
    id: "inkin",
    tradition: "Japanese Zen",
    name: "Procession Bell",
    traditionalName: "Inkin (引磬)",
    use: "transitions · ceremony · kinhin",
    decayLabel: "3 s",
    character: "mid · clear · directive",
  },
  {
    id: "ogane",
    tradition: "Japanese Zen",
    name: "Great Temple Bell",
    traditionalName: "Ōgane (大鐘)",
    use: "major transitions · retreat framing",
    decayLabel: "16 s",
    character: "deep · vast · solemn",
  },
  {
    id: "han",
    tradition: "Japanese Zen",
    name: "Life-and-Death Board",
    traditionalName: "Han (板)",
    use: "retreat opening · sesshin framing",
    decayLabel: "2 s / strike",
    character: "wooden · urgent · grave",
  },
  {
    id: "hyoshigi",
    tradition: "Japanese Zen",
    name: "Walking Clappers",
    traditionalName: "Hyōshigi (拍子木)",
    use: "kinhin start · kinhin end",
    decayLabel: "< 0.5 s",
    character: "sharp · wooden · precise",
  },
  {
    id: "mokugyo",
    tradition: "Japanese Zen",
    name: "Wooden Fish",
    traditionalName: "Mokugyo (木魚)",
    use: "chanting rhythm · pacing",
    decayLabel: "< 1 s",
    character: "hollow · dry · steady",
  },

  // ── Tibetan / Vajrayana ──────────────────────────────────────────────────────
  {
    id: "singing_bowl",
    tradition: "Tibetan",
    name: "Himalayan Singing Bowl",
    traditionalName: "Singing Bowl (頌缽)",
    use: "opening · settling · transitions",
    decayLabel: "10 s",
    character: "warm · resonant · vast",
  },
  {
    id: "tingsha",
    tradition: "Tibetan",
    name: "Clarity Cymbals",
    traditionalName: "Tingsha (丁夏)",
    use: "clearing · mindfulness · puja",
    decayLabel: "5 s",
    character: "high · bright · piercing",
  },
  {
    id: "drilbu",
    tradition: "Tibetan",
    name: "Ritual Bell",
    traditionalName: "Drilbu (དྲིལ་བུ)",
    use: "puja opening · deity invocation",
    decayLabel: "4 s",
    character: "mid · clear · ceremonial",
  },
  {
    id: "damaru",
    tradition: "Tibetan",
    name: "Double Drum",
    traditionalName: "Damaru (ཌཱ་མ་རུ)",
    use: "Chöd · Vajrayana offering",
    decayLabel: "< 1 s / beat",
    character: "rapid · primal · awakening",
  },

  // ── Chinese Mahayana ────────────────────────────────────────────────────────
  {
    id: "yunqing",
    tradition: "Chinese Mahayana",
    name: "Cloud Gong",
    traditionalName: "Yúnqìng (雲磬)",
    use: "ceremony · procession marker",
    decayLabel: "3.5 s",
    character: "metallic · bright · airy",
  },
  {
    id: "zhong",
    tradition: "Chinese Mahayana",
    name: "Temple Bell",
    traditionalName: "Zhōng (鐘)",
    use: "morning 108 strikes · major transitions",
    decayLabel: "12 s",
    character: "deep · bronze · resonant",
  },

  // ── Korean ───────────────────────────────────────────────────────────────────
  {
    id: "moktak",
    tradition: "Korean",
    name: "Wooden Block",
    traditionalName: "Moktak (목탁)",
    use: "chanting rhythm · assembly",
    decayLabel: "< 1 s",
    character: "hollow · sharp · steady",
  },
  {
    id: "beomjong",
    tradition: "Korean",
    name: "Dharma Bell",
    traditionalName: "Beomjong (범종)",
    use: "morning service · major openings",
    decayLabel: "16 s",
    character: "deep · resonant · majestic",
  },

  // ── Vipassana (Goenka tradition) ─────────────────────────────────────────────
  {
    id: "vipassana_gong",
    tradition: "Vipassana",
    name: "Goenka Gong",
    traditionalName: "Vipassana Gong",
    use: "session start · session end · Adhitthana",
    decayLabel: "10 s",
    character: "warm · full · steady",
  },
];

/** Ordered list of tradition groups for UI grouping */
export const BELL_TRADITION_ORDER: string[] = [
  "Japanese Zen",
  "Tibetan",
  "Chinese Mahayana",
  "Korean",
  "Vipassana",
];
